import { useState, useEffect, useRef } from 'react';
import { TutorSession, TutorMessage } from '@homework-app/shared';
import { apiService } from '../../services/api';
import { getApiBaseUrl } from '../../constants';
import ChatSidebar from './ChatSidebar';
import ChatMessage from './ChatMessage';
import PhotoCapture from './PhotoCapture';
import './TutorPage.css';

interface TutorPageProps {
  onClose: () => void;
}

const API_BASE = getApiBaseUrl();

export default function TutorPage({ onClose }: TutorPageProps) {
  const [sessions, setSessions] = useState<TutorSession[]>([]);
  const [currentSession, setCurrentSession] = useState<TutorSession | null>(null);
  const [messages, setMessages] = useState<TutorMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  // Focus input when session changes
  useEffect(() => {
    if (currentSession) {
      inputRef.current?.focus();
    }
  }, [currentSession]);

  const loadSessions = async () => {
    try {
      const data = await apiService.getTutorSessions();
      setSessions(data);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  };

  const loadMessages = async (sessionId: string) => {
    try {
      const data = await apiService.getTutorMessages(sessionId);
      setMessages(data);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError('Failed to load messages');
    }
  };

  const GREETING_MESSAGE: TutorMessage = {
    id: 'greeting',
    session_id: '',
    role: 'assistant',
    content: "Hey Taylor! 👋 I'm your homework tutor. I'm here to help you understand your homework - not give you the answers! Tell me what you're working on, or snap a photo of your assignment, and let's figure it out together.",
    created_at: new Date().toISOString(),
  };

  const createNewSession = async () => {
    try {
      const session = await apiService.createTutorSession();
      setSessions(prev => [session, ...prev]);
      setCurrentSession(session);
      setMessages([{ ...GREETING_MESSAGE, session_id: session.id }]);
      setShowSidebar(false);
    } catch (err) {
      console.error('Failed to create session:', err);
      setError('Failed to create new chat');
    }
  };

  const selectSession = async (session: TutorSession) => {
    setCurrentSession(session);
    setMessages([]);
    setShowSidebar(false);
    await loadMessages(session.id);
  };

  const updateSessionTitle = async (sessionId: string, title: string) => {
    try {
      await apiService.updateTutorSessionTitle(sessionId, title);
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? { ...s, title } : s
      ));
      if (currentSession?.id === sessionId) {
        setCurrentSession(prev => prev ? { ...prev, title } : prev);
      }
    } catch (err) {
      console.error('Failed to update session title:', err);
    }
  };

  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputMessage.trim() && !selectedImage) return;
    if (isLoading) return;

    // Create session if needed
    let session = currentSession;
    if (!session) {
      try {
        session = await apiService.createTutorSession();
        setSessions(prev => [session!, ...prev]);
        setCurrentSession(session);
      } catch (err) {
        console.error('Failed to create session:', err);
        setError('Failed to start chat');
        return;
      }
    }

    const messageText = inputMessage.trim();
    const imageFile = selectedImage;

    // Add user message to UI immediately
    const tempUserMessage: TutorMessage = {
      id: `temp-${Date.now()}`,
      session_id: session.id,
      role: 'user',
      content: messageText,
      image_url: imagePreview,
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, tempUserMessage]);
    setInputMessage('');
    clearImage();
    setIsLoading(true);
    setStreamingMessage('');
    setError(null);

    try {
      await apiService.sendTutorMessage(
        session.id,
        messageText,
        imageFile || undefined,
        // onChunk
        (chunk) => {
          setStreamingMessage(prev => prev + chunk);
        },
        // onComplete
        () => {
          // Reload messages to get proper IDs and update session title
          loadMessages(session!.id);
          loadSessions();
          setStreamingMessage('');
          setIsLoading(false);
        },
        // onError
        (error) => {
          setError(error);
          setIsLoading(false);
          setStreamingMessage('');
        }
      );
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
      setIsLoading(false);
      setStreamingMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <div className="tutor-page">
      {/* Header */}
      <header className="tutor-header">
        <button
          className="tutor-menu-btn"
          onClick={() => setShowSidebar(!showSidebar)}
          aria-label="Toggle chat history"
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
          </svg>
        </button>
        <h1>Homework Tutor</h1>
        <button className="tutor-close-btn" onClick={onClose}>
          Back to Homework
        </button>
      </header>

      <div className="tutor-content">
        {/* Sidebar */}
        <ChatSidebar
          sessions={sessions}
          currentSession={currentSession}
          onSelectSession={selectSession}
          onNewChat={createNewSession}
          onUpdateTitle={updateSessionTitle}
          isOpen={showSidebar}
          onClose={() => setShowSidebar(false)}
        />

        {/* Main chat area */}
        <main className="tutor-main">
          {/* Messages */}
          <div className="tutor-messages">
            {!currentSession && messages.length === 0 && (
              <div className="tutor-welcome">
                <h2>Hey Taylor! I'm your homework tutor.</h2>
                <p>
                  I'm here to help you understand your homework - not to give you the answers!
                  I'll ask you questions and guide you to figure things out yourself.
                </p>
                <p>
                  You can type a question or take a photo of your homework to get started.
                </p>
                <div className="tutor-suggestions">
                  <button onClick={() => setInputMessage("I don't understand my math homework")}>
                    Help with math
                  </button>
                  <button onClick={() => setInputMessage("Can you explain this science concept?")}>
                    Science question
                  </button>
                  <button onClick={() => setInputMessage("I need help with my reading assignment")}>
                    Reading help
                  </button>
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                apiBase={API_BASE}
              />
            ))}

            {streamingMessage && (
              <div className="chat-message assistant">
                <div className="message-avatar">AI</div>
                <div className="message-content">
                  <div className="message-text">{streamingMessage}</div>
                </div>
              </div>
            )}

            {isLoading && !streamingMessage && (
              <div className="chat-message assistant">
                <div className="message-avatar">AI</div>
                <div className="message-content">
                  <div className="message-text typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="tutor-error">
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Image preview */}
          {imagePreview && (
            <div className="image-preview-container">
              <img src={imagePreview} alt="Selected homework" className="image-preview" />
              <button className="image-preview-remove" onClick={clearImage}>
                Remove
              </button>
            </div>
          )}

          {/* Input area */}
          <form className="tutor-input-form" onSubmit={handleSubmit}>
            <PhotoCapture onImageSelect={handleImageSelect} />
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about your homework..."
              rows={1}
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || (!inputMessage.trim() && !selectedImage)}
              className="send-btn"
            >
              <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </form>
        </main>
      </div>
    </div>
  );
}
